import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Phone, Mail, MessageSquare, MapPin, CheckCircle2, Zap, X } from "lucide-react";

export default function Sprint() {
  const navigate = useNavigate();
  const [duration, setDuration] = useState(30);
  const [running, setRunning] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [xp, setXp] = useState(0);
  const intRef = useRef(null);

  const start = async () => {
    const { data } = await api.get("/prospects");
    const prioritized = data.filter((p) => p.status !== "Converti" && p.status !== "Sans suite").sort((a, b) => b.score - a.score).slice(0, 10);
    setProspects(prioritized);
    setRunning(true);
    setRemaining(duration * 60);
    setIndex(0);
    intRef.current = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
  };

  useEffect(() => () => intRef.current && clearInterval(intRef.current), []);

  const current = prospects[index];
  const next = () => {
    if (index + 1 >= prospects.length) {
      setIndex(prospects.length);
      clearInterval(intRef.current);
    } else {
      setIndex((i) => i + 1);
    }
  };

  const markDone = async (type = "general") => {
    if (!current) return;
    await api.post("/interactions", { prospect_id: current.prospect_id, type, notes: "Sprint" });
    setDone((d) => [...d, current.prospect_id]);
    setXp((x) => x + 10);
    next();
  };

  const defer = () => {
    if (!current) return;
    setProspects((p) => [...p.slice(0, index), ...p.slice(index + 1), current]);
  };

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  if (!running) {
    return (
      <div className="min-h-screen bg-[var(--surface-alt)] grid place-items-center p-5">
        <div className="bg-[var(--surface)] rounded-xl p-8 max-w-md w-full border border-[var(--border-soft)] shadow-sm text-center">
          <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-emerald-500 to-red-500 grid place-items-center mx-auto">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[var(--text)] mt-4">Action Booster</h1>
          <p className="text-sm text-zinc-500 mt-1">Un sprint intensif pour faire progresser votre tunnel.</p>
          <div className="mt-5 grid grid-cols-3 gap-2">
            {[30, 60, 120].map((d) => (
              <button key={d} onClick={() => setDuration(d)} data-testid={`dur-${d}`} className={`rounded-2xl p-4 border ${duration === d ? "bg-emerald-500 text-white border-emerald-500" : "bg-[var(--surface)] text-zinc-300 border-[var(--border)]"}`}>
                <div className="font-display text-xl font-bold">{d >= 60 ? `${d / 60}h` : `${d}min`}</div>
              </button>
            ))}
          </div>
          <Button onClick={start} className="w-full rounded-full bg-emerald-500/100 hover:bg-emerald-500 mt-5 h-12 font-semibold" data-testid="start-sprint">Démarrer le sprint</Button>
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="w-full rounded-full mt-2">Retour</Button>
        </div>
      </div>
    );
  }

  if (index >= prospects.length || remaining === 0) {
    return (
      <div className="min-h-screen bg-[var(--surface-alt)] grid place-items-center p-5">
        <div className="bg-[var(--surface)] rounded-xl p-8 max-w-md w-full border border-[var(--border-soft)] shadow-sm text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
          <h1 className="font-display text-2xl font-bold text-[var(--text)] mt-4">Sprint terminé 🎉</h1>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="bg-[var(--surface-alt)] rounded-2xl p-3">
              <div className="font-display text-xl font-bold text-[var(--text)]">{done.length}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Complétées</div>
            </div>
            <div className="bg-[var(--surface-alt)] rounded-2xl p-3">
              <div className="font-display text-xl font-bold text-[var(--text)]">{skipped.length}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Skippées</div>
            </div>
            <div className="bg-emerald-500/10 rounded-2xl p-3">
              <div className="font-display text-xl font-bold text-emerald-400">+{xp}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-300">XP</div>
            </div>
          </div>
          <Button onClick={() => navigate("/dashboard")} className="w-full rounded-full bg-emerald-500/100 hover:bg-emerald-500 mt-5 h-11 font-semibold" data-testid="finish-sprint">Retour dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-alt)] flex flex-col">
      <div className="bg-gradient-to-r from-emerald-500 to-red-500 text-white p-4 flex items-center justify-between">
        <button onClick={() => { clearInterval(intRef.current); setRunning(false); }} className="p-2"><X className="w-5 h-5" /></button>
        <div className="text-center">
          <div className="font-display text-2xl font-bold" data-testid="sprint-timer">{mm}:{ss}</div>
          <div className="text-xs opacity-90">{index + 1}/{prospects.length} · +{xp} XP</div>
        </div>
        <div className="w-9" />
      </div>

      <div className="flex-1 max-w-2xl mx-auto w-full p-5 flex items-center">
        <div className="bg-[var(--surface)] rounded-xl p-6 w-full border border-[var(--border-soft)] shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 grid place-items-center text-white text-xl font-bold">{(current.name || "?").charAt(0)}</div>
            <div>
              <div className="font-display font-bold text-lg text-[var(--text)]">{current.name}</div>
              <div className="text-sm text-zinc-500">{current.company}</div>
              <div className="text-xs text-zinc-500 mt-1">Score : {current.score} · Dernier contact : {current.last_contact ? new Date(current.last_contact).toLocaleDateString("fr-FR") : "—"}</div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2">
            {current.phone && <a href={`tel:${current.phone}`} onClick={() => markDone("call")} data-testid="sprint-call" className="rounded-2xl p-3 bg-emerald-500/100 text-white font-semibold text-xs text-center"><Phone className="w-4 h-4 mx-auto mb-1" />Appeler</a>}
            {current.email && <a href={`mailto:${current.email}`} onClick={() => markDone("email")} data-testid="sprint-email" className="rounded-2xl p-3 bg-sky-500/100 text-white font-semibold text-xs text-center"><Mail className="w-4 h-4 mx-auto mb-1" />Email</a>}
            <button onClick={() => { navigate(`/social/${current.prospect_id}`); }} data-testid="sprint-msg" className="rounded-2xl p-3 bg-emerald-500/100 text-white font-semibold text-xs text-center"><MessageSquare className="w-4 h-4 mx-auto mb-1" />Msg</button>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2">
            <Button onClick={() => markDone("general")} className="rounded-full bg-emerald-500/100 hover:bg-emerald-600 font-semibold" data-testid="action-done">✓ Fait</Button>
            <Button onClick={defer} variant="outline" className="rounded-full font-semibold" data-testid="action-defer">→ Reporter</Button>
            <Button onClick={() => { setSkipped((s) => [...s, current.prospect_id]); next(); }} variant="outline" className="rounded-full font-semibold" data-testid="action-skip">✗ Skip</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
